import { useDisclosure } from '@talos/kyoko';
import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';

export interface AlertAction {
  title: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

export interface AlertProps {
  title?: string;
  message?: string;
  actions?: AlertAction[];
  onDismiss?: () => void;
}

interface OpenAlertProps extends AlertProps {
  content?: ReactNode;
}

const AlertContext = createContext<
  | ({
      content?: ReactNode;
      open: (params?: OpenAlertProps) => void;
    } & AlertProps &
      Omit<ReturnType<typeof useDisclosure>, 'open'>)
  | undefined
>(undefined);

/**
 * Hook to open global alerts that are not tied to a specific component.
 * @returns The global alert context, which can be used to manage a global alert.
 */
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('Missing Alert.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
};

const DEFAULT_PARAMS: AlertProps = {
  title: '',
  message: '',
  actions: [
    {
      title: 'OK',
      style: 'default',
    },
  ],
  onDismiss: () => {},
};

/**
 * Provider is used to open global alerts that are not tied to a specific component.
 */
export const AlertProvider = function AlertProvider({ children }: { children: ReactNode }) {
  const paramsRef = useRef<Partial<OpenAlertProps>>();
  const alert = useDisclosure();

  const openAlert = useCallback(
    (params?: OpenAlertProps) => {
      paramsRef.current = { ...DEFAULT_PARAMS, ...params };
      alert.open();
    },
    [alert]
  );

  return (
    <AlertContext.Provider
      value={{
        ...alert,
        open: openAlert,
        ...paramsRef.current,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
